import React, { useEffect, useRef } from 'react';
import { Row, Col, Typography, Checkbox, Form, Input, Select } from 'antd';
import ReactGA from 'react-ga';

import states from '../../../states';
import { useSetRecoilState } from 'recoil';

import imagePlaceholder from '../../../img/image-placeholder.png';

const CustomizeExerciseItem = ({
  isExerciseFrequency,
  exercise,
  checkedExercises,
  handleCheckExerciseItem,
}) => {
  const setPrescription = useSetRecoilState(states.prescription);
  const prevExercise = useRef();
  const [form] = Form.useForm();

  // update all fields values when previous and current exercsies is not identical
  useEffect(() => {
    prevExercise.current = exercise;

    if (prevExercise !== exercise) {
      if (checkedExercises.length !== 0) {
        form.setFieldsValue({
          sets: exercise.sets,
          reps: exercise.reps,
          hold: exercise.hold,
          instructions: exercise.instructions,
        });
      }
    }
  }, [exercise, checkedExercises]);

  // handle field values changes
  const handleChange = (name, value) => {
    setPrescription(prevState => ({
      ...prevState,
      form: {
        ...prevState.form,
        prescription: {
          ...prevState.form.prescription,
          exercises: prevState.form.prescription.exercises.map(ex =>
            ex.id === exercise.id ? { ...ex, [name]: value } : ex
          ),
        },
      },
    }));
  };

  return (
    <div className="customize-exercise-item">
      <Row gutter={[16, 16]} style={{ marginTop: -5, marginBottom: -5 }}>
        <Col lg={1} md={1} />
        <Col lg={23} md={23}>
          <Typography.Title level={5}>{exercise.exerciseName}</Typography.Title>
        </Col>
      </Row>
      <Row gutter={[32, 32]}>
        <Col lg={1} md={1} className="center-content">
          <Checkbox
            onChange={() => handleCheckExerciseItem(exercise.id)}
            checked={checkedExercises.includes(exercise.id)}
          />
        </Col>
        <Col lg={5} md={5} className="center-content">
          <img
            style={{ width: '100%', aspectRatio: 16 / 10 }}
            className="exercise-thumbnail"
            crossOrigin="anonymous"
            alt="exercise"
            src={`https://d2p805pqn3eul9.cloudfront.net/${exercise.groupId}/${exercise.id}.jpg`}
            onError={e => {
              ReactGA.event({
                category: 'ImageError',
                action: `${exercise.groupId}/${exercise.id}`,
              });
              const proxy = `https://s3.amazonaws.com/ptwired-exercise-images-prod/${exercise.groupId}/${exercise.id}.jpg`;
              if (e.target.src === proxy) {
                e.target.src = imagePlaceholder;
              } else {
                e.target.src = proxy;
              }
            }}
          />
        </Col>
        <Col lg={18} md={18}>
          <Form form={form} layout="vertical">
            <Row gutter={[16, 16]}>
              <Col lg={12} md={12}>
                <Typography.Text>Parameters</Typography.Text>
                <Row
                  gutter={[8, 8]}
                  className="mt-1"
                  style={{ marginBottom: -8 }}
                >
                  <Col lg={8} md={8}>
                    <Form.Item name="sets">
                      <Input
                        size="large"
                        placeholder="Enter sets"
                        value={exercise.sets}
                        defaultValue={exercise.sets}
                        onChange={e => handleChange('sets', e.target.value)}
                        type="text"
                        name="sets"
                        suffix="sets"
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={8} md={8}>
                    <Form.Item name="reps">
                      <Input
                        size="large"
                        placeholder="Enter reps"
                        value={exercise.reps}
                        defaultValue={exercise.reps}
                        onChange={e => handleChange('reps', e.target.value)}
                        type="text"
                        name="reps"
                        suffix="reps"
                      />
                    </Form.Item>
                  </Col>
                  <Col lg={8} md={8}>
                    <Form.Item name="hold">
                      <Input
                        size="large"
                        placeholder="Enter hold"
                        value={exercise.hold}
                        defaultValue={exercise.hold}
                        onChange={e => handleChange('hold', e.target.value)}
                        type="text"
                        name="hold"
                        suffix="hold"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                {isExerciseFrequency && (
                  <React.Fragment>
                    <Typography.Text>Prescription Frequency</Typography.Text>
                    <div className="mt-1">
                      <Select
                        size="large"
                        value={exercise.frequency}
                        defaultValue={exercise.frequency}
                        onChange={value => handleChange('frequency', value)}
                        virtual={false}
                        name="frequency"
                        style={{ minWidth: 120 }}
                      >
                        <Select.Option value={1}>1x</Select.Option>
                        <Select.Option value={2}>2x</Select.Option>
                        <Select.Option value={3}>3x</Select.Option>
                        <Select.Option value={4}>4x</Select.Option>
                        <Select.Option value={5}>5x</Select.Option>
                        <Select.Option value={6}>6x</Select.Option>
                        <Select.Option value={7}>7x</Select.Option>
                        <Select.Option value={8}>8x</Select.Option>
                        <Select.Option value={9}>9x</Select.Option>
                        <Select.Option value={10}>10x</Select.Option>
                      </Select>
                      <span className="ml-2 mr-2">Every</span>
                      <Select
                        size="large"
                        value={exercise.perdiem}
                        defaultValue={exercise.perdiem}
                        onChange={value => handleChange('perdiem', value)}
                        virtual={false}
                        name="perdiem"
                        style={{ minWidth: 120 }}
                      >
                        <Select.Option value={'day'}>Day</Select.Option>
                        <Select.Option value={'week'}>Week</Select.Option>
                        <Select.Option value={'month'}>Month</Select.Option>
                      </Select>
                    </div>
                  </React.Fragment>
                )}
              </Col>

              <Col lg={12} md={12}>
                <Form.Item
                  name="instructions"
                  label="Prescription Instructions"
                >
                  <Input.TextArea
                    value={exercise.instructions}
                    defaultValue={exercise.instructions}
                    onChange={e => handleChange('instructions', e.target.value)}
                    size="large"
                    placeholder="Enter instructions"
                    rows={4}
                    showCount
                    maxLength={750}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default CustomizeExerciseItem;
